import React from 'react';

import { useRouter } from 'next/router';

import AccountBenefitsModal from 'components/account/AccountBenefitsModal';
import PhoneVerificationModal from 'components/account/PhoneVerificationModal';
import AddressGeolocationConfirmModal from 'components/address/AddressGeolocationConfirmModal';
import NewsletterSubscribeModal from 'components/common/NewsletterSubscribeModal';
import GeniusFastCheckoutModal from 'components/genius/GeniusFastCheckoutModal';
import GeniusTrialModal from 'components/genius/GeniusTrialModal';
import OrderFeedbackModal from 'components/orders/OrderFeedbackModal';
import OrderGiftsModal from 'components/orders/OrderGiftsModal';
import ProductQuantityVariantsModal from 'components/product/ProductQuantityVariantsModal';
import SimilarProductsModal from 'components/product/SimilarProductsModal';
import PromotionsModal from 'components/promotions/PromotionsModal';
import RecommendationsModal from 'components/recommendation/RecommendationsModal';
import { useModalDispatch, useModalStore } from 'context/ModalContext';
import { MODAL_TYPES } from 'helpers/LayoutHelpers';

import type { IModalListItem } from 'context/ModalContext';
import type { IControlledModalProps } from 'types';

interface ModalItem {
  id: IModalListItem['id'],
  Component: React.ComponentType<IControlledModalProps>
}

const modals: ModalItem[] = [
  { id: MODAL_TYPES.ACCOUNT_BENEFITS, Component: AccountBenefitsModal },
  { id: MODAL_TYPES.AVAILABLE_PROMOTIONS, Component: PromotionsModal },
  { id: MODAL_TYPES.GENIUS_FAST_CHECKOUT, Component: GeniusFastCheckoutModal },
  { id: MODAL_TYPES.GENIUS_TRIAL, Component: GeniusTrialModal },
  { id: MODAL_TYPES.ORDER_GIFTS, Component: OrderGiftsModal },
  { id: MODAL_TYPES.PHONE_VERIFICATION, Component: PhoneVerificationModal },
  { id: MODAL_TYPES.PRODUCT_VARIANTS, Component: ProductQuantityVariantsModal },
  { id: MODAL_TYPES.RECOMMENDATIONS, Component: RecommendationsModal },
  { id: MODAL_TYPES.SIMILAR_PRODUCTS, Component: SimilarProductsModal },
  { id: MODAL_TYPES.NEWSLETTER_SUBSCRIBE, Component: NewsletterSubscribeModal },
  { id: MODAL_TYPES.ORDER_FEEDBACK, Component: OrderFeedbackModal },
  { id: MODAL_TYPES.ADDRESS_CONFIRM_GEOLOCATION, Component: AddressGeolocationConfirmModal }
];

const ModalContainer = () => {
  return (
    <>
      {
        modals.map(({ id, Component }) => {
          return (
            <RemoteModal
              key={`controlled-modal-${id}`}
              id={id}
              Component={Component}
            />
          );
        })
      }
    </>
  );
};

interface RemoteModalProps {
  id: IModalListItem['id'],
  Component: React.ComponentType<IControlledModalProps>
}

export function RemoteModal(props: RemoteModalProps) {
  const {
    id,
    Component
  } = props;

  // Hooks

  const router = useRouter();
  const [isVisible, setVisible] = React.useState(false);

  const { stack } = useModalStore();
  const { removeModalFromStack } = useModalDispatch();

  // Props

  const isOpen = stack.contains(id);
  const { props: componentProps } = stack.getById(id) || {};

  // State Hooks

  const [persistedProps, setPersistedProps] = React.useState(componentProps);

  // Handlers

  const handleClose = React.useCallback(() => {
    if (isOpen) {
      if (componentProps?.onClose) {
        componentProps.onClose();
        return;
      }
      removeModalFromStack(id);
    }
  }, [componentProps, removeModalFromStack, id, isOpen]);

  const handleAfterClose = React.useCallback(() => {
    if (componentProps?.onAfterClose) {
      componentProps.onAfterClose();
    }
    setVisible(false);
    setPersistedProps({});
  }, [componentProps]);

  // Effects

  React.useEffect(() => {
    if (isOpen) {
      setPersistedProps(componentProps);
      setVisible(true);
    }
  }, [isOpen, componentProps]);

  React.useEffect(() => {
    router.events.on('routeChangeStart', handleClose);
    return () => {
      router.events.off('routeChangeStart', handleClose);
    };
  }, [
    router.events,
    handleClose
  ]);

  // Empty

  if (!isVisible) return null;

  // Render

  return (
    <Component
      {...persistedProps}
      //
      id={id}
      isOpen={isOpen}
      //
      onClose={handleClose}
      onAfterClose={handleAfterClose}
    />
  );
}

export default ModalContainer;
